/**
 * Beam enterprise_info parsing + shared form defaults.
 * Kept separate from Constants to limit circular-init issues (Constants imports route components).
 */
import { COUNTRY_CODE } from '../Constants/Constants';

/** Mirrors Django BillingFrequency / `BILLING_FREQUENCY_ENUM` for Beam forms. */
export const BEAM_BILLING_FREQUENCY = {
  QUARTERLY: 4,
  MONTHLY: 12,
  HALF_YEARLY: 2,
  ANNUAL: 1
};

export const BEAM_CATEGORY_PRICING_TYPES = {
  FRESH: 'fresh',
  ADDENDUM: 'addendum'
};

export const DFY_MODEL_SELECT_ITEMS = [
  { value: 'usage_based', label: 'DFY usage-based' },
  { value: 'license_based', label: 'DFY license-based' }
];

/** Same as `DFY_MODEL_SELECT_ITEMS` — kept for call sites that still use this name. */
export const DFY_MODEL_ITEMS = DFY_MODEL_SELECT_ITEMS;

export const getEmptyBeamContractPricing = () => ({
  bpt_contract_amount: '',
  workable_sheet_cost: '',
  addendum_sheet_cost: '',
  manual_worksheet_cost: '',
  billing_frequency: BEAM_BILLING_FREQUENCY.QUARTERLY,
  trade_wise_pricing: {
    others: {
      [BEAM_CATEGORY_PRICING_TYPES.FRESH]: 0,
      [BEAM_CATEGORY_PRICING_TYPES.ADDENDUM]: 0
    }
  }
});

export const normalizeEnterpriseBillingDetails = bd => {
  if (!bd || typeof bd !== 'object') return {};
  return {
    email: bd.email ?? bd.billing_email ?? '',
    name: bd.name ?? bd.company_name ?? '',
    address: bd.address ?? '',
    city: bd.city ?? '',
    zip: bd.zip ?? bd.postal_code ?? '',
    country: bd.country ?? COUNTRY_CODE.USA
  };
};

export const coerceEnterpriseEmailList = value => {
  if (value == null || value === '') return [];
  if (Array.isArray(value)) {
    return value
      .map(entry => {
        if (entry == null) return '';
        if (typeof entry === 'string') return entry.trim().toLowerCase();
        if (typeof entry === 'object' && entry.email != null) return String(entry.email).trim().toLowerCase();
        return String(entry).trim().toLowerCase();
      })
      .filter(Boolean);
  }
  if (typeof value === 'string')
    return value
      .split(/[,\n]/)
      .map(s => s.trim().toLowerCase())
      .filter(Boolean);
  return [];
};

export const unwrapEnterpriseInfoPayload = raw => {
  if (raw == null || typeof raw !== 'object') return {};
  const looksLikePayload = obj =>
    obj &&
    typeof obj === 'object' &&
    (obj.commercial_setup != null ||
      obj.commercialSetup != null ||
      obj.company_info != null ||
      obj.companyInfo != null ||
      obj.shared_contract_terms != null ||
      obj.sharedContractTerms != null ||
      obj.enterprise_contract != null ||
      obj.enterpriseContract != null ||
      obj.billing_details != null ||
      obj.billingDetails != null ||
      obj.summary_tiles != null ||
      obj.summaryTiles != null ||
      obj.company != null ||
      (obj.company_vertical != null && obj.company_vertical !== '') ||
      (obj.account_segment != null && obj.account_segment !== '') ||
      obj.enterprise_attributes != null ||
      obj.enterpriseAttributes != null);

  let cur = raw;
  for (let depth = 0; depth < 6 && !looksLikePayload(cur); depth += 1) {
    const next =
      cur.data != null && typeof cur.data === 'object'
        ? cur.data
        : cur.results != null && typeof cur.results === 'object'
          ? cur.results
          : cur.payload != null && typeof cur.payload === 'object'
            ? cur.payload
            : cur.result != null && typeof cur.result === 'object'
              ? cur.result
              : null;
    if (next == null) break;
    cur = next;
  }
  return cur;
};

export const mergeSharedContractTermsForForm = data => {
  if (!data || typeof data !== 'object') return {};
  const sct = data.shared_contract_terms || data.sharedContractTerms || {};
  const ec = data.enterprise_contract || data.enterpriseContract || {};
  return {
    ...sct,
    contract_start_date: sct.contract_start_date ?? ec.contract_start_date ?? ec.start_date ?? ec.contractStartDate,
    contract_end_date: sct.contract_end_date ?? ec.contract_end_date ?? ec.end_date ?? ec.contractEndDate,
    platform_access_end_date:
      sct.platform_access_end_date ??
      ec.platform_access_end_date ??
      ec.platformAccessEndDate ??
      data.platform_access?.end_date ??
      data.platformAccess?.end_date,
    seats: sct.seats ?? ec.seats,
    total_users: sct.total_users ?? ec.total_users
  };
};

export const pickPlatformAccessFromEnterpriseInfo = data => {
  if (!data || typeof data !== 'object') return {};
  const pa = data.platform_access || data.platformAccess || {};
  return {
    start_date: pa.start_date ?? pa.startDate,
    end_date: pa.end_date ?? pa.endDate
  };
};

export const resolveCompanyInfoSliceFromEnterpriseInfo = data => {
  if (!data || typeof data !== 'object') return {};

  return {
    business_unit: data.business_unit,
    account_segment: data.account_segment,
    key_account_managers: data.key_account_managers,
    onboarding_specialist: data.onboarding_specialists
  };
};

export const extractBillingDetailsFromEnterpriseInfo = data => {
  if (!data || typeof data !== 'object') return {};
  const nested =
    data.billing_details ?? data.billingDetails ?? data.enterprise?.billing_details ?? data.enterprise?.billingDetails;
  let norm = normalizeEnterpriseBillingDetails(nested);
  if (norm.name || norm.email || norm.address) return norm;
  norm = normalizeEnterpriseBillingDetails({
    name: nested?.name ?? data.company?.name ?? data.company_name,
    email: nested?.email ?? data.billing_email,
    address: nested?.address ?? data.address ?? data.street_address ?? data.line1,
    city: nested?.city ?? data.city,
    zip: nested?.zip ?? data.zip ?? data.postal_code,
    country: nested?.country ?? data.country
  });
  if (norm.name || norm.email || norm.address) return norm;
  return normalizeEnterpriseBillingDetails(nested);
};
